import { useTranslation } from "@/language"
/**
 * SessionBookingsModal Component
 * Displays a modal showing users who have booked a coaching session
 * with tabs for Upcoming/Completed and search functionality
 */

import { Search, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { ChatBubbleIcon, MoneyIcon, PencilIcon, SearchIcon } from "@/components/icons"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import { useCancelCoachingBooking, useInitiateSessionRefund } from "../hooks/useCoachingBooking"
import { type BookingTab, useCoachingSessionBookings } from "../hooks/useCoachingBookings"
import { AdminEditBookingModal, type BookingDetails } from "./AdminEditBookingModal"

export interface SessionBookingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string
  sessionTitle: string
  coverImage?: string | null
  participationFee: string
  price: number | null
  duration: number
  format: string
  initialTab?: BookingTab
  expert?: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl?: string | null
  }
}

interface BookingRowProps {
  id: string
  date: string
  startTime: string
  endTime: string
  user: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
    email: string | null
  }
  message?: string | null
  tab: BookingTab
  isPaid: boolean
  hasRefund: boolean
  onChatClick: (userId: string) => void
  onEditClick: (booking: BookingDetails) => void
  onRefundClick: (bookingId: string) => void
}

function BookingRow({
  id,
  date,
  startTime,
  endTime,
  user,
  tab,
  isPaid,
  hasRefund,
  onChatClick,
  onEditClick,
  onRefundClick,
  message,
}: BookingRowProps) {
  const { t } = useTranslation("coaching")
  // Format date to display format (e.g., "Jan 12, 2026")
  const formatDate = (dateStr: string) => {
    if (!dateStr) return ""
    const d = new Date(dateStr)
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  // Format time to display format (e.g., "09:00 AM - 10:00 AM")
  const formatTime = (start: string, end: string) => {
    if (!start || !end) return ""
    const formatTimePart = (time: string) => {
      const [hours, minutes] = time.split(":")
      const h = parseInt(hours, 10)
      const ampm = h >= 12 ? "PM" : "AM"
      const hour12 = h % 12 || 12
      return `${hour12}:${minutes} ${ampm}`
    }
    return `${formatTimePart(start)} - ${formatTimePart(end)}`
  }

  const displayName = user.name || user.username || "Unknown"
  const avatarFallback = displayName[0]?.toUpperCase() || "?"

  return (
    <div className="flex items-center justify-between py-5 px-6 border-b border-[#F2F0EB]/10 last:border-b-0">
      {/* User Info */}
      <div className="flex items-center gap-3.75">
        <Avatar
          src={user.profilePictureUrl ?? undefined}
          fallback={avatarFallback}
          size="md"
          className="w-10 h-10"
        />
        <div className="flex flex-row gap-2.5">
          <span className="text-[14px] font-semibold text-[#F2F0EB] leading-5">{displayName}</span>
          {user.username && (
            <span className="text-[14px] text-[#F2F0EB]/50 leading-5">@{user.username}</span>
          )}
        </div>
      </div>

      {/* Date/Time and Actions */}
      <div className="flex items-center gap-5">
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-[14px] text-[#F2F0EB] leading-5">{formatDate(date)}</span>
          <span className="text-[14px] text-[#F2F0EB]/50 leading-5">
            {formatTime(startTime, endTime)}
          </span>
        </div>

        {/* Action Icons - Different based on tab */}
        <div className="flex items-center gap-2">
          {/* Chat Icon - always shown */}
          <button
            type="button"
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            aria-label={t("chat")}
            onClick={() => onChatClick(user.id)}
          >
            <ChatBubbleIcon size={22} color="#FFFFFF" />
          </button>

          {tab === "upcoming" ? (
            /* Edit Icon - only for upcoming */
            <button
              type="button"
              className="w-6 h-6 flex items-center justify-center cursor-pointer transition-opacity"
              aria-label={t("edit")}
              onClick={() =>
                onEditClick({
                  id,
                  date,
                  startTime,
                  endTime,
                  user,
                  message,
                })
              }
            >
              <PencilIcon size={18} color="#FFFFFF" />
            </button>
          ) : isPaid && !hasRefund ? (
            /* Refund text + Money icon - only for completed PAID sessions without refund */
            <div
              className="flex items-center justify-center gap-1.5 cursor-pointer"
              onClick={() => onRefundClick(id)}
            >
              <MoneyIcon size={22} color="#C86757" />
              <span className="text-[14px] text-[#C86757] font-normal">Refund</span>
            </div>
          ) : hasRefund ? (
            <span className="text-[14px] text-[#C86757] font-normal">Requested</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function SessionBookingsModal({
  open,
  onOpenChange,
  sessionId,
  sessionTitle,
  coverImage,
  participationFee,
  price,
  duration,
  format: _format,
  initialTab = "upcoming",
  expert,
}: SessionBookingsModalProps) {
  const navigate = useNavigate()
  const createDmConversation = useCreateDmConversation()
  const [activeTab, setActiveTab] = useState<BookingTab>(initialTab)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const [editBooking, setEditBooking] = useState<BookingDetails | null>(null)
  const { t } = useTranslation("coaching")

  const initiateRefund = useInitiateSessionRefund()
  const cancelBooking = useCancelCoachingBooking()

  // Handle chat click - create/get DM and navigate
  const handleChatClick = async (userId: string) => {
    try {
      const conversation = await createDmConversation.mutateAsync(userId)
      navigate(`/chats/dm/${conversation.id}`)
    } catch (error) {
      console.error("Failed to open chat:", error)
    }
  }

  // Handle edit click - open edit modal
  const handleEditClick = (booking: BookingDetails) => {
    setEditBooking(booking)
  }

  // Handle refund click
  const handleRefundClick = (bookingId: string) => {
    initiateRefund.mutate(bookingId, {
      onSuccess: () => {
        toast.success("Refund initiated successfully")
      },
    })
  }

  // Handle edit modal close
  const handleEditModalClose = (isOpen: boolean) => {
    if (!isOpen) {
      setEditBooking(null)
    }
  }

  // Handle cancel session from AdminCancelSessionModal
  const handleCancelSession = (bookingId: string, message?: string) => {
    cancelBooking.mutate(
      { bookingId, message },
      {
        onSuccess: () => {
          setEditBooking(null)
          onOpenChange(false)
          toast.success("Session cancelled successfully")
        },
        onError: () => {
          toast.error("Failed to cancel session")
        },
      },
    )
  }

  // Sync activeTab when initialTab prop changes (e.g., when switching between Bookings/Completed clicks)
  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  // Debounce search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    // Simple debounce - in production could use a proper debounce hook
    setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  // Toggle search visibility
  const toggleSearch = () => {
    setIsSearchVisible(prev => !prev)
    if (isSearchVisible) {
      // Clear search when hiding
      setSearchQuery("")
      setDebouncedSearch("")
    }
  }

  // Fetch bookings
  const { data, isLoading } = useCoachingSessionBookings(sessionId, {
    search: debouncedSearch || undefined,
    tab: activeTab,
    enabled: open && !!sessionId,
  })

  // Format price display
  const isFree = participationFee === "Free"
  const priceDisplay = isFree ? "Free" : price ? `$${Math.round(price)}` : participationFee

  // Format duration display (e.g., "1 hr 30 min")
  const formatDuration = (mins: number) => {
    const hours = Math.floor(mins / 60)
    const minutes = mins % 60
    const parts: string[] = []
    if (hours > 0) {
      parts.push(`${hours} hr`)
    }
    if (minutes > 0) {
      parts.push(`${minutes} min`)
    }
    return parts.join(" ") || "0 min"
  }

  const durationDisplay = formatDuration(duration)

  const tabs = [
    { key: "upcoming" as const, label: "UPCOMING", count: data?.counts.upcoming ?? 0 },
    { key: "completed" as const, label: "COMPLETED", count: data?.counts.completed ?? 0 },
  ]

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-full lg:min-w-[900px] p-0 bg-[#0F0F0F] border border-[#F2F0EB]/30 rounded-tr-[30px] rounded-bl-[30px] overflow-hidden md:min-h-[725px] min-h-[500px] flex flex-col space-y-0"
          overlayClass="bg-[rgba(0,0,0,0.9)] backdrop-blur-xl"
          showCloseButton={false}
        >
          <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
            {/* Header with close button */}
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-2.5">
                {/* Session Info with Image */}
                <div className="flex items-center gap-4">
                  {/* Session Thumbnail */}
                  {coverImage && (
                    <div className="w-[90px] h-[60px] rounded-[10px] overflow-hidden shrink-0">
                      <img
                        src={coverImage}
                        alt={sessionTitle}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-2.5">
                    <h2 className="text-[16px] font-semibold text-[#F2F0EB]/90 leading-5">
                      {sessionTitle}
                    </h2>
                    {/* Meta info */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[13px] ${
                          isFree ? "bg-brand-olive text-white" : "bg-brand-terracotta text-white"
                        } font-normal`}
                      >
                        {priceDisplay}
                      </span>
                      <span className="text-[13px] text-[#F2F0EB]/90">Private Session</span>
                      <span className="text-[13px] text-[#F2F0EB]/90">{durationDisplay}</span>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onOpenChange(false)}
                className="w-10 h-10 rounded-full bg-white/5 border border-[#F2F0EB]/15 p-0 hover:bg-white/10 border-gradient-3d"
                aria-label={t("close")}
              >
                <X className="!w-5.5 !h-5.5 text-[#F2F0EB]" />
              </Button>
            </div>
          </DialogHeader>

          {/* Tabs and Search */}
          <div className="flex items-center justify-between px-6 border-b border-[#F2F0EB]/10 shrink-0">
            {/* Tabs - Text only, no icons */}
            <div className="flex items-center gap-5">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`text-[15px] font-semibold uppercase font-semibold leading-6 py-4 cursor-pointer transition-opacity ${
                    activeTab === tab.key
                      ? "text-[#F2F0EB] border-b-1 border-[#F2F0EB]"
                      : "text-[#F2F0EB]/50 border-b-1 border-transparent"
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Search - Hidden by default, shown when search icon clicked */}
            <div className="flex items-center gap-1">
              {/* Search input - only visible when isSearchVisible is true */}
              {isSearchVisible && (
                <div className="relative animate-in fade-in slide-in-from-right-2 duration-200">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F2F0EB]/50" />
                  <Input
                    type="text"
                    placeholder={t("search")}
                    value={searchQuery}
                    onChange={e => handleSearchChange(e.target.value)}
                    className="w-45 h-10 pl-10 pr-10 bg-white/5 border border-[#F2F0EB]/15 rounded-lg"
                    style={{ color: "#F2F0EB" }}
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery("")
                        setDebouncedSearch("")
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      aria-label={t("clearSearch")}
                    >
                      <X className="w-4 h-4 text-[#F2F0EB]/50" />
                    </button>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={toggleSearch}
                className="p-2 rounded-md transition-colors cursor-pointer"
                aria-label={t("toggleSearch")}
              >
                {isSearchVisible ? (
                  <X className="w-5 h-5 text-[#F2F0EB]/50" />
                ) : (
                  <SearchIcon className="w-5 h-5 text-[#F2F0EB]/50 opacity-50" />
                )}
              </button>
            </div>
          </div>

          {/* Booking List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 px-6">
                <div className="text-sm text-[#F2F0EB]/50">Loading...</div>
              </div>
            ) : data?.bookings && data.bookings.length > 0 ? (
              <div className="">
                {data.bookings.map(booking => (
                  <BookingRow
                    key={booking.id}
                    id={booking.id}
                    date={booking.date}
                    startTime={booking.startTime}
                    endTime={booking.endTime}
                    user={booking.user}
                    message={booking.message ?? undefined}
                    tab={activeTab}
                    isPaid={participationFee === "Paid"}
                    hasRefund={booking.hasRefund ?? false}
                    onChatClick={handleChatClick}
                    onEditClick={handleEditClick}
                    onRefundClick={handleRefundClick}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-sm text-[#F2F0EB]/50">
                  {searchQuery
                    ? `No results found for "${searchQuery}"`
                    : activeTab === "upcoming"
                      ? "No upcoming bookings"
                      : "No completed bookings"}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <AdminEditBookingModal
        open={!!editBooking}
        onOpenChange={handleEditModalClose}
        booking={editBooking}
        session={{
          id: sessionId,
          title: sessionTitle,
          coverImage,
          duration,
          participationFee: participationFee as "Free" | "Paid",
          price,
          expert,
        }}
        onCancelSession={handleCancelSession}
        isCancelling={cancelBooking.isPending}
      />
    </>
  )
}
